import {
  ExtendedReportType,
  ExtendedReportUsage,
  getPhaseAwareReportTypeLabel,
  InterimReportPhase,
} from "@/data/models/Report";

/**
 * How IRES Office names a report and words its dates: the project's Berichte tab (`ReportItem.vue`)
 * and the admin portal's report list both go through here, so a report reads the same on both.
 * Moved out of `ReportItem.vue` unchanged, pinned by `tests/unit/reportItemLabel.spec.ts` (written
 * against the component before the move).
 */

/** The report fields the name depends on: a stored `Report`, or a portal list row. */
export interface ReportLabelFields {
  reportType?: string | null;
  usage?: string | null;
  extendedReportType?: string | null;
  extendedReportUsage?: string | null;
  interimReportPhase?: string | null;
  phaseComplete?: boolean | null;
  fileName?: string | null;
  fileNameToDisplay?: string | null;
}

type Translate = (key: string, params?: string[]) => string;

/** The usage that counts: the extended one when present, else the plain one. */
export function getReportUsage(report: ReportLabelFields): string | null | undefined {
  return report.extendedReportUsage ?? report.usage;
}

/** The usage name, or for a usage named by its file, the file name (a fallback key otherwise). */
function fileNameOr(report: ReportLabelFields, fallback: string): string {
  return report.fileNameToDisplay ?? report.fileName ?? fallback;
}

/**
 * The report's name, e.g. "Zwischenbericht Trocknung (extern)", or for an uploaded document its
 * file name in brackets; a single time entry is named by its file alone.
 */
export function formatReportLabel(report: ReportLabelFields, t: Translate): string {
  let reportType = "";
  const extendedReportType = report.extendedReportType ?? report.reportType;

  switch (extendedReportType) {
    case ExtendedReportType.INTERIM_REPORT: {
      reportType = report.interimReportPhase === InterimReportPhase.INTERIM_REPAIR
        ? t("report.interimReportRepair")
        : t("report.interimReportDrying");
      break;
    }
    case ExtendedReportType.FINAL_REPORT: {
      reportType = t("report.finalReportDrying");
      break;
    }
    case ExtendedReportType.DAMAGE_REPORT: {
      reportType = t("report.damageReport");
      break;
    }
    case ExtendedReportType.LEAK_DETECTION_REPORT: {
      reportType = t("report.leakDetectionReport");
      break;
    }
    case ExtendedReportType.TIME_TRACKING: {
      reportType = t("report.timeTracking");
      break;
    }
    case ExtendedReportType.DRYING: {
      reportType = "";
      break;
    }

    default: {
      reportType = t("common.unknown");
      break;
    }
  }

  let reportUsage = "";
  const extendedReportUsage = getReportUsage(report);

  switch (extendedReportUsage) {
    case ExtendedReportUsage.INTERNAL:
      reportUsage = t("report.reportUsage.internalDescription");
      break;
    case ExtendedReportUsage.EXTERNAL:
      reportUsage = t("report.reportUsage.externalDescription");
      break;
    case ExtendedReportUsage.EXT_IMAGES:
      reportUsage = t("report.reportUsage.externalImagesDescription");
      break;
    case ExtendedReportUsage.EXT_SHORT:
      reportUsage = t("report.reportUsage.externalShortDescription");
      break;
    case ExtendedReportUsage.EXT_TIME_TRACKING:
      reportUsage = t("report.reportUsage.externalTimeTrackingDescription");
      break;
    case ExtendedReportUsage.DOCUMENT:
      reportUsage = fileNameOr(report, "");
      break;
    case ExtendedReportUsage.SERVICE:
      reportUsage = t("report.reportUsage.serviceDescription");
      break;
    case ExtendedReportUsage.SERVICE_JSON:
      reportUsage = t("report.reportUsage.serviceJsonDescription");
      break;
    case ExtendedReportUsage.MEASUREMENT:
      reportUsage = t("report.reportUsage.measurementProtocol");
      break;
    case ExtendedReportUsage.HY_DRY_CERTIFICATE:
      reportUsage = t("report.reportUsage.hyDryCertificate");
      break;
    case ExtendedReportUsage.LO:
      reportUsage = fileNameOr(report, t("report.reportUsage.leakDetection"));
      break;
    case ExtendedReportUsage.SA:
      reportUsage = fileNameOr(report, t("report.reportUsage.damageAssessment"));
      break;
    case ExtendedReportUsage.TA:
      reportUsage = fileNameOr(report, t("report.reportUsage.drying"));
      break;
    case ExtendedReportUsage.WH:
      reportUsage = fileNameOr(report, t("report.reportUsage.restoration"));
      break;
    case ExtendedReportUsage.EXT_SINGLE_TIME_ENTRY:
      reportUsage = fileNameOr(report, t("report.reportUsage.externalSingleTimeTrackingDescription"));
      break;
    case ExtendedReportUsage.SV_IMAGE:
      reportUsage = fileNameOr(report, t("report.reportUsage.svImage"));
      break;

    default:
      reportUsage = fileNameOr(report, t("common.unknown"));
      break;
  }

  if (extendedReportUsage === ExtendedReportUsage.EXT_SINGLE_TIME_ENTRY) {
    return reportUsage;
  }

  // Override report type prefix based on phase completion status.
  // Keeps the subject (e.g. "Trocknung") but swaps the prefix (e.g. "Bericht" / "Zwischenbericht").
  // Applies to all extended report types except time entries and documents.
  const phaseAwareLabel = getPhaseAwareReportTypeLabel(
    report.phaseComplete,
    extendedReportType as ExtendedReportType,
    extendedReportUsage as ExtendedReportUsage,
    report.interimReportPhase as InterimReportPhase | null | undefined,
    t("report.completedPhaseReport"),
    t("report.interimPhaseReport"),
    {
      drying: t("report.subject.drying"),
      repair: t("report.subject.repair"),
      damageAssessment: t("report.subject.damageAssessment"),
      leakDetection: t("report.subject.leakDetection"),
    },
  );
  if (phaseAwareLabel != null) {
    reportType = phaseAwareLabel;
  }

  return reportType + " (" + reportUsage + ")";
}

/** Whether the usage is one that goes out to the customer, so its send date is shown too. */
export function isExternalReportUsage(usage: string | null | undefined): boolean {
  switch (usage) {
    case ExtendedReportUsage.EXTERNAL:
    case ExtendedReportUsage.EXT_IMAGES:
    case ExtendedReportUsage.EXT_SHORT:
    case ExtendedReportUsage.EXT_TIME_TRACKING:
    case ExtendedReportUsage.SERVICE:
    case ExtendedReportUsage.SERVICE_JSON:
      return true;

    default:
      return false;
  }
}

/**
 * "erstellt am X", or for a sent or external report creation and send date (one date when they fall
 * on the same day). The dates arrive formatted, so each screen keeps its own date format.
 *
 * @param formattedReportDate - the creation date, formatted.
 * @param formattedLastTimeSend - the send date, formatted; null when never sent.
 * @param isExternal - from {@link isExternalReportUsage}.
 */
export function formatReportDateText(
  formattedReportDate: string,
  formattedLastTimeSend: string | null,
  isExternal: boolean,
  t: Translate
): string {
  if (formattedLastTimeSend !== null || isExternal) {
    const sent = formattedLastTimeSend ?? formattedReportDate;

    return t(formattedReportDate === sent ? "report.formatReportDateAndSendDate" : "report.formatReportDateWithSendDate", [
      formattedReportDate,
      sent,
    ]);
  }

  return t("report.formatReportDate", [formattedReportDate]);
}
